import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorMessage } from "../api";
import type { CollectionSummary, Item, Meeting } from "../api/types";
import { EmptyState, ErrorBox, Modal, Spinner } from "../components/bits";
import { MenuButton } from "../components/ContextMenu";
import { useItemMenu } from "../components/ItemMenu";
import { formatYmd } from "../editor/tasktokens";
import { formatDateTime, formatTime } from "../lib/format";
import { createCollection, createItem, toastError, upsertItems } from "../store/app";
import { useAllCollections, useApp } from "../store/hooks";

const MEETINGS_NAME = "Meetings";
const NOTES_TEMPLATE = "## Agenda\n\n- \n\n## Notes\n\n\n## Action items\n\n";

/** Find the "Meetings" notebook in a space, creating it if missing. */
export async function ensureMeetingsCollection(spaceId: string, collections: CollectionSummary[]): Promise<string> {
  const existing = collections.find(
    (c) => c.space_id === spaceId && c.name.toLowerCase() === MEETINGS_NAME.toLowerCase(),
  );
  if (existing) return existing.id;
  const d = await createCollection(spaceId, MEETINGS_NAME, "notebook");
  return d.collection.id;
}

const TASK_COL_KEY = "tia.meeting.taskCollection";

export function lastTaskCollection(): string | null {
  try {
    return localStorage.getItem(TASK_COL_KEY);
  } catch {
    return null;
  }
}
export function rememberTaskCollection(id: string): void {
  try {
    localStorage.setItem(TASK_COL_KEY, id);
  } catch {
    /* ignore */
  }
}

/** Collections that can receive meeting tasks: non-archived, not notebooks. */
export function taskTargetCollections(collections: CollectionSummary[]): CollectionSummary[] {
  return collections.filter((c) => !c.archived_at && c.kind !== "notebook");
}

/** Default "Tasks go to": last used (if still available), else first list/board of the space. */
export function defaultTaskCollection(collections: CollectionSummary[], spaceId: string | null): string {
  const targets = taskTargetCollections(collections);
  const last = lastTaskCollection();
  if (last && targets.some((c) => c.id === last)) return last;
  const inSpace = targets.filter((c) => c.space_id === spaceId);
  return (
    inSpace.find((c) => c.kind === "list" || c.kind === "board")?.id ??
    inSpace[0]?.id ??
    targets.find((c) => c.kind === "list" || c.kind === "board")?.id ??
    targets[0]?.id ??
    ""
  );
}

/** <select> of task target collections, grouped by space. */
export function TaskCollectionSelect({
  value,
  onChange,
  collections,
  id,
  className,
  ariaLabel,
  required,
  emptyLabel = "Choose a collection…",
}: {
  emptyLabel?: string;
  value: string;
  onChange: (id: string) => void;
  collections: CollectionSummary[];
  id?: string;
  className?: string;
  ariaLabel?: string;
  required?: boolean;
}) {
  const spaces = useApp((s) => s.spaces) ?? [];
  const targets = taskTargetCollections(collections);
  const known = targets.some((c) => c.id === value);
  return (
    <select
      id={id}
      className={className}
      aria-label={ariaLabel}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
    >
      {!value && <option value="">{emptyLabel}</option>}
      {value && !known && <option value={value}>(unavailable collection)</option>}
      {spaces.map((sp) => {
        const cols = targets.filter((c) => c.space_id === sp.id);
        if (!cols.length) return null;
        return (
          <optgroup key={sp.id} label={sp.name}>
            {cols.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

// Meeting times are only in ItemDetail.meeting; cache them per item.
const meetingCache = new Map<string, Meeting | null>();

export function NewMeetingDialog({ onClose }: { onClose: () => void }) {
  const spaceId = useApp((s) => s.currentSpaceId ?? s.spaces?.[0]?.id ?? null);
  const collections = useAllCollections();
  const navigate = useNavigate();
  const now = new Date();
  const nextHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(formatYmd(nextHour));
  const [start, setStart] = useState(`${pad(nextHour.getHours())}:00`);
  const [end, setEnd] = useState(`${pad((nextHour.getHours() + 1) % 24)}:00`);
  const [location, setLocation] = useState("");
  const [onlineUrl, setOnlineUrl] = useState("");
  const [taskCol, setTaskCol] = useState(() => defaultTaskCollection(collections, spaceId));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!taskCol) setTaskCol(defaultTaskCollection(collections, spaceId));
  }, [collections, spaceId, taskCol]);

  return (
    <Modal title="New meeting" onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!spaceId) return toastError(new Error("No space available"));
          if (!taskCol) return toastError(new Error("Choose where the meeting's tasks go"));
          setBusy(true);
          try {
            const colId = await ensureMeetingsCollection(spaceId, collections);
            const starts = start ? new Date(`${date}T${start}`).toISOString() : null;
            let ends = end ? new Date(`${date}T${end}`).toISOString() : null;
            if (starts && ends && ends <= starts) ends = new Date(new Date(ends).getTime() + 86400000).toISOString();
            const item = await createItem(colId, {
              type: "meeting",
              title: title.trim() || "Meeting",
              body: NOTES_TEMPLATE,
              start_at: starts,
              meeting: {
                starts_at: starts,
                ends_at: ends,
                location: location.trim() || null,
                online_url: onlineUrl.trim() || null,
                task_collection_id: taskCol,
              },
            });
            rememberTaskCollection(taskCol);
            onClose();
            navigate(`/meetings/${item.id}`);
          } catch (err) {
            toastError(err, "Could not create meeting");
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </label>
        <div className="field-row">
          <label className="field">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </label>
          <label className="field">
            <span>Start</span>
            <input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="field">
            <span>End</span>
            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span>Tasks go to</span>
          <TaskCollectionSelect value={taskCol} onChange={setTaskCol} collections={collections} required />
          <span className="muted small">Task lines in the notes become tasks in this collection (unless a line names a +project).</span>
        </label>
        <label className="field">
          <span>Location</span>
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Room 2.14" />
        </label>
        <label className="field">
          <span>Online meeting URL</span>
          <input type="url" value={onlineUrl} onChange={(e) => setOnlineUrl(e.target.value)} placeholder="https://…" />
        </label>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Creating…" : "Create meeting"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function MeetingsPage() {
  const collections = useAllCollections();
  const items = useApp((s) => s.items);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [meetings, setMeetings] = useState<Record<string, Meeting | null>>({});
  const colKey = collections.map((c) => c.id).join(",");

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const results = await Promise.all(
        collections.map((c) => api.items(c.id, { type: "meeting", include_done: true }).catch(() => ({ items: [] }))),
      );
      const all = results.flatMap((r) => r.items).filter((i) => i.type === "meeting");
      upsertItems(all);
      // Fetch meeting details (times) for those not cached yet.
      const missing = all.filter((i) => !meetingCache.has(i.id)).slice(0, 50);
      await Promise.all(
        missing.map((i) =>
          api
            .item(i.id)
            .then((d) => meetingCache.set(i.id, d.meeting))
            .catch(() => meetingCache.set(i.id, null)),
        ),
      );
      setMeetings(Object.fromEntries(all.map((i) => [i.id, meetingCache.get(i.id) ?? null])));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (collections.length) void load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colKey]);

  const list = useMemo(() => {
    const ms = Object.values(items).filter((i) => i.type === "meeting");
    const startOf = (id: string, fallback: string | null) => meetings[id]?.starts_at ?? fallback ?? "";
    return ms.sort((a, b) => startOf(b.id, b.start_at ?? b.created_at).localeCompare(startOf(a.id, a.start_at ?? a.created_at)));
  }, [items, meetings]);

  const nowIso = new Date().toISOString();
  const upcoming = list.filter((m) => (meetings[m.id]?.starts_at ?? m.start_at ?? "") >= nowIso).reverse();
  const past = list.filter((m) => !upcoming.includes(m));

  const renderList = (ms: typeof list) => (
    <ul className="meeting-list">
      {ms.map((m) => {
        const meta = meetings[m.id];
        const starts = meta?.starts_at ?? m.start_at;
        return (
          <MeetingRow key={m.id} meeting={m}>
            <span className="meeting-when">
              {starts ? formatDateTime(starts) : "No date"}
              {meta?.ends_at && ` – ${formatTime(meta.ends_at)}`}
            </span>
            <span className="meeting-title">{m.title}</span>
            {meta?.location && <span className="muted small">{meta.location}</span>}
            <span className="muted small">{collections.find((c) => c.id === m.collection_id)?.name}</span>
          </MeetingRow>
        );
      })}
    </ul>
  );

  return (
    <div className="page">
      <header className="page-head">
        <h1>Meetings</h1>
        <button type="button" className="btn primary" onClick={() => setShowNew(true)}>
          New meeting
        </button>
      </header>
      {error && <ErrorBox error={error} onRetry={load} />}
      {loading && list.length === 0 ? (
        <Spinner />
      ) : list.length === 0 ? (
        <EmptyState title="No meetings yet">
          <p className="muted">Create a meeting to take notes. Task lines in the notes become real tasks.</p>
        </EmptyState>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section className="group">
              <h2 className="group-head plain">Upcoming</h2>
              {renderList(upcoming)}
            </section>
          )}
          {past.length > 0 && (
            <section className="group">
              <h2 className="group-head plain">Past</h2>
              {renderList(past)}
            </section>
          )}
        </>
      )}
      {showNew && <NewMeetingDialog onClose={() => setShowNew(false)} />}
    </div>
  );
}

/** A meeting in the list: link plus right-click / "⋯" menu (open, copy link, delete…). */
function MeetingRow({ meeting, children }: { meeting: Item; children: ReactNode }) {
  const menu = useItemMenu(meeting);
  return (
    <li className={`meeting-li${menu.isOpen ? " menu-open" : ""}`}>
      <Link to={`/meetings/${meeting.id}`} className="meeting-row" {...menu.targetProps}>
        {children}
      </Link>
      <MenuButton label={`${meeting.title || "(untitled)"} actions`} expanded={menu.isOpen} onOpen={menu.openFrom} className="row-more" />
      {menu.menu}
    </li>
  );
}
